import React from 'react';
import { Activity, TrendingUp, Users, Zap, DollarSign } from 'lucide-react';
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './AdminAnalytics.css';

const AdminAnalytics: React.FC = () => {
  // Dummy data for charts
  const usageData = [
    { date: 'Jan', requests: 4000, tokens: 120000 },
    { date: 'Feb', requests: 3000, tokens: 98000 },
    { date: 'Mar', requests: 5000, tokens: 150000 },
    { date: 'Apr', requests: 7000, tokens: 210000 },
    { date: 'May', requests: 6000, tokens: 180000 },
    { date: 'Jun', requests: 8000, tokens: 240000 },
  ];

  const modelUsage = [
    { name: 'GPT-4o', value: 35, color: '#2563eb' },
    { name: 'Claude', value: 30, color: '#7c3aed' },
    { name: 'Gemini', value: 20, color: '#059669' },
    { name: 'Others', value: 15, color: '#f59e0b' },
  ];

  const topUsers = [
    { email: 'user1@example.com', requests: 15000, plan: 'Enterprise' },
    { email: 'user2@example.com', requests: 12000, plan: 'Pro' },
    { email: 'user3@example.com', requests: 10000, plan: 'Pro' },
    { email: 'user4@example.com', requests: 8000, plan: 'Free' },
    { email: 'user5@example.com', requests: 6000, plan: 'Pro' },
  ];

  return (
    <div className="admin-analytics">
      <div className="page-header">
        <div>
          <h1>Analytics & Reports</h1>
          <p>Detailed usage statistics and insights</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4" style={{ marginBottom: '2rem' }}>
        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Total Requests</span>
            <Activity size={20} style={{ color: 'var(--color-primary)' }} />
          </div>
          <div className="stats-card-value">33,000</div>
          <div className="stats-card-change positive">
            <TrendingUp size={16} />
            +12% from last month
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Total Tokens</span>
            <Zap size={20} style={{ color: 'var(--color-warning)' }} />
          </div>
          <div className="stats-card-value">998K</div>
          <div className="stats-card-change positive">
            <TrendingUp size={16} />
            +8% from last month
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Active Users</span>
            <Users size={20} style={{ color: 'var(--color-success)' }} />
          </div>
          <div className="stats-card-value">127</div>
          <div className="stats-card-change positive">
            <TrendingUp size={16} />
            +5 new users
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Revenue</span>
            <DollarSign size={20} style={{ color: 'var(--color-success)' }} />
          </div>
          <div className="stats-card-value">$2,450</div>
          <div className="stats-card-change positive">
            <TrendingUp size={16} />
            +18% growth
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2" style={{ marginBottom: '2rem' }}>
        <div className="card">
          <div className="card-header">
            <h2>Usage Trends</h2>
            <p>Requests and tokens over time</p>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={usageData}>
                <defs>
                  <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorTokens" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="requests" stroke="#2563eb" fillOpacity={1} fill="url(#colorRequests)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Model Usage Distribution</h2>
            <p>Popular AI models</p>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={modelUsage}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {modelUsage.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Users */}
      <div className="card">
        <div className="card-header">
          <h2>Top Users by Requests</h2>
          <p>Most active API users</p>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Total Requests</th>
                <th>Usage %</th>
              </tr>
            </thead>
            <tbody>
              {topUsers.map((user, idx) => (
                <tr key={idx}>
                  <td>
                    <div className="rank-badge">{idx + 1}</div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge badge-${user.plan === 'Enterprise' ? 'warning' : user.plan === 'Pro' ? 'primary' : 'secondary'}`}>
                      {user.plan}
                    </span>
                  </td>
                  <td>{user.requests.toLocaleString()}</td>
                  <td>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${(user.requests / 15000) * 100}%` }}
                      ></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;